import { Api } from "@/api";
import { Currency } from "@/generated/graphql.lazy/b2c";
import { CurrenciesDocument } from "@/generated/graphql.lazy/generic";
import { Context } from "@/services/context.ts";
import { createContext } from "preact";
import { defaultCurrency } from "@/constants/currency.ts";

export const CURRENCY_SERVICE_LISTENER_NAME = "currency";

export class CurrencyService {
  private rates = new Map<string, number>();
  private isLoadingState = false;
  public allCurrenciesIsLoaded = false;

  currencies: Currency[] = [];

  constructor(
    protected context: Context,
    protected api: Api,
  ) {
    if (!import.meta.env.SSR) {
      window.addEventListener("route:load-end", () => this.fetchAllCurrencies());
    }
  }

  public async fetchAllCurrencies() {
    if (this.allCurrenciesIsLoaded || this.isLoadingState) {
      return;
    }

    if (this.currencies.length) {
      this.allCurrenciesIsLoaded = true;
      return;
    }

    this.isLoadingState = true;

    await this.load();

    this.context.notify(CURRENCY_SERVICE_LISTENER_NAME);
    this.isLoadingState = false;
    this.allCurrenciesIsLoaded = true;
  }

  private async fetchCurrencies() {
    const response = await this.api.client.query({
      query: CurrenciesDocument,
      variables: {},
    });

    return response.data.Currencies;
  }

  public async load() {
    const currencies = await this.fetchCurrencies();

    this.setPreloadedCurrencies(currencies as Currency[]);
  }

  setPreloadedCurrencies(currencies: Currency[]) {
    this.currencies = currencies;
    this.setRates(currencies);
  }

  clear() {
    this.currencies = [];
    this.rates.clear();
  }

  private setRates = (data: Array<{ code: string; rate: number }>) => {
    for (const item of data) {
      this.rates.set(item.code, item.rate);
    }
  };

  public getRates() {
    return this.rates;
  }

  public getRate(code: string) {
    const rate = this.rates.get(code);

    if (rate == null) {
      return this.rates.get(defaultCurrency);
    }

    return rate;
  }
}

export const CurrencyServiceContext = createContext<CurrencyService>("currency-service");
